import * as Logger from "js-logger";
import { Signal } from "signals";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Layers from "slotworks/graphics/layers/layers";
import { AnticipationResult } from "slotworks/model/gameplay/records/results/anticipation-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import { RandomRangeInt } from "slotworks/utils/math/random";
import Timer from "slotworks/utils/timer";

export default class AnimateReelTransition extends AbstractReelTransition {

    public static ANTICIPATION_DELAY: number = 500;
    public static anticipationLayer: Layers;

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private anticipationResult: AnticipationResult;
    private anticipationAnimations: AnimatedSprite[] = [];
    private heldReels: number[];
    private anticipating: boolean;

    private pendingLands: number = 0;
    private resolveOnLand: Function;

    private stopTimers: number[];

    constructor(startGap: number, stopGap: number, quickStopGap: number) {
        super();

        this.onReelLand = new Signal();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.cancelSpin();
    }

    public start(reelset?: string[][], jumpStart: boolean = false): Contract<void> {
        Services.sound.event(SoundEvent.reel_transition_start);

        const startFuncs = [];

        let n = -1;
        for (let i = 1; i <= this.reels.length; i++) {
            startFuncs.push(() => new Contract<void>((resolve) => {
                Timer.setTimeout(() => {
                    if (this.heldReels && this.heldReels.indexOf(i) > -1) {
                        resolve(null);
                    } else {
                        Services.sound.event(SoundEvent.reel_N_spin_start, i.toString());
                        Services.sound.event(SoundEvent.reel_any_spin_start);

                        this.spinReel(i).then(() => resolve(null));
                        n++;
                    }
                }, jumpStart ? 0 : this.startGap * n);
            }));
        }

        return new Parallel(startFuncs);
    }

    public stop(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {

        this.anticipating = false;

        const anticipatations: AnticipationResult[] = spinRecord.getResultsOfType(AnticipationResult);
        this.anticipationResult = anticipatations[0];

        const stopFuncs: Array<() => Contract<void>> = [];

        this.stopTimers = [];

        for (let i = 1; i <= this.reels.length; i++) {
            const isHeld = this.heldReels && this.heldReels.indexOf(i) > -1;
            const anticipate = this.anticipationResult.reelAnticipations[i - 1];

            stopFuncs.push(() => new Contract<void>((resolve) => {

                let stopDelay = quickSpin ? this.quickStopGap : this.stopGap;

                if (!isHeld) {

                    if (anticipate) {
                        stopDelay += AnimateReelTransition.ANTICIPATION_DELAY;
                        this.anticipate(i);
                    }

                    const timer = Timer.setTimeout(() => {

                        this.stopAnticipation(i);

                        this.stopReel(i, spinRecord.stops, spinRecord.reelset, this.anticipationResult).then(() => {
                            for (const symbol of this.reels[i - 1].getSymbols()) {
                                symbol.setSymbol(spinRecord.reelset[i - 1][spinRecord.stops[i - 1]]);
                                symbol.updateTransform();
                                symbol.stop().execute();
                                if (this.anticipationResult.landSymbols[i - 1].indexOf(symbol.symbolId) > -1) {
                                    symbol.land().execute();
                                }
                            }

                            Services.sound.event(SoundEvent.reel_N_spin_end, i.toString());
                            Services.sound.event(SoundEvent.reel_any_spin_end);

                            resolve(null);
                        });
                    }, stopDelay);

                    this.stopTimers.push(timer);
                } else {
                    resolve(null);
                }
            }));
        }

        return new Contract<void>((resolve) => {
            new Sequence(stopFuncs).then(() => {
                Services.sound.event(SoundEvent.reel_transition_end);

                this.stopAnticipations();
                resolve(null);
            });
        });
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        Services.sound.event(SoundEvent.reel_transition_skip);

        for (const timer of this.stopTimers) {
            Timer.clearTimeout(timer);
        }

        const stopFuncs: Array<() => Contract<void>> = [];

        for (let i = 1; i <= this.reels.length; i++) {
            if (this.getReelSpinAnim(i).visible) {
                stopFuncs.push(() => this.stopReel(i, spinRecord.stops, spinRecord.reelset, this.anticipationResult));
            }
        }

        return new Contract<void>((resolve) => {
            new Sequence(stopFuncs).then(() => {
                Services.sound.event(SoundEvent.reel_transition_skip_end);

                this.stopAnticipations();
                if (this.pendingLands === 0) {
                    resolve(null);
                } else {
                    this.resolveOnLand = resolve;
                }
            });
        });
    }

    public jump(stops: number[], reelset: string[][]) {
        this.cancelSpin();

        for (let i = 0; i < stops.length; i++) {
            for (const symbol of this.reels[i].getSymbols()) {
                symbol.setSymbol(reelset[i][stops[i]]);
            }
        }
    }

    public hold(reelIndexes?: number[]) {
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();
    }

    private spinReel(index: number) {
        const reelSpin = this.getReelSpinAnim(index);
        reelSpin.gotoAndPlay(RandomRangeInt(1, reelSpin.totalFrames - 1));
        reelSpin.visible = true;

        this.setReelSymbolsVisible(index, false);

        return Contract.empty();
    }

    private stopReel(index: number, stops: number[], reelset: string[][], anticipation: AnticipationResult) {
        const reelSpin = this.getReelSpinAnim(index);
        reelSpin.stop();
        reelSpin.visible = false;

        this.setReelSymbolsVisible(index, true);

        for (const symbol of this.reels[index - 1].getSymbols()) {
            symbol.setSymbol(reelset[index - 1][stops[index - 1]]);
            symbol.stop().execute();
            if (anticipation && anticipation.landSymbols[index - 1].indexOf(symbol.symbolId) > -1) {
                this.pendingLands++;
                symbol.land().then(() => {
                    this.pendingLands--;
                    if (this.pendingLands <= 0 && this.resolveOnLand) {
                        this.resolveOnLand();
                        this.resolveOnLand = null;
                    }
                });
            }
        }

        return Contract.empty();
    }

    private anticipate(reelIndex: number) {

        if (!this.anticipating) {
            this.anticipating = true;
            Services.sound.event(SoundEvent.reel_transition_anticipate);
        }
        Services.sound.event(SoundEvent.reel_any_anticipate_start);
        Services.sound.event(SoundEvent.reel_N_anticipate_start, reelIndex.toString());

        const anticipationLayer = AnimateReelTransition.anticipationLayer || this.animationLayer;
        const anticipationAnimation = anticipationLayer.getAnimatedSprite("anticipation_" + reelIndex.toString());
        if (anticipationAnimation) {
            anticipationAnimation.visible = true;
            anticipationAnimation.loop = true;
            anticipationAnimation.play();
            this.anticipationAnimations[reelIndex - 1] = anticipationAnimation;
        }
    }

    private stopAnticipation(reelIndex: number) {

        const anticipation = (this.anticipationAnimations.length > reelIndex - 1) ? this.anticipationAnimations[reelIndex - 1] : null;
        if (anticipation) {
            Services.sound.event(SoundEvent.reel_any_anticipate_end);
            Services.sound.event(SoundEvent.reel_N_anticipate_end, reelIndex.toString());

            anticipation.stop();
            anticipation.visible = false;
        }
        this.anticipationAnimations[reelIndex - 1] = null;
    }

    private stopAnticipations() {
        for (let i = 1; i <= this.anticipationAnimations.length; i++) {
            this.stopAnticipation(i);
        }
    }

    private getReelSpinAnim(index: number) {
        const anim = this.matrixLayer.getAnimatedSprite("reelspin_" + index.toString());
        if (!anim) {
            Logger.error("reelspin_" + index.toString() + " does not exist");
        }

        return anim;
    }

    private setReelSymbolsVisible(reelIndex: number, visible: boolean) {
        const symbols = this.reels[reelIndex - 1].getSymbols();

        for (const symbol of symbols) {
            symbol.setVisible(visible);
            symbol.updateTransform();
        }
    }

    private cancelSpin() {
        for (let i = 1; i <= this.reels.length; i++) {
            const reelSpin = this.getReelSpinAnim(i);
            reelSpin.stop();
            reelSpin.visible = false;
            this.setReelSymbolsVisible(i, true);
        }
    }
}
